import type { RsvpInput, RsvpRecord } from '@axorain/shared';
import { getIdToken } from '../auth/session';
import { config } from '../config';

/**
 * Thin RSVP API client. Every call carries the Cognito ID token as a Bearer credential;
 * the API derives identity (sub/email/name) from that token, never from the body — so the
 * request shape is exactly the guest-controlled `RsvpInput` from the shared contract.
 */
async function authed(path: string, init?: RequestInit): Promise<Response> {
  const token = await getIdToken();
  return fetch(`${config.apiUrl}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${token}`,
      ...init?.headers,
    },
  });
}

/** Surface the API's `{ error, detail }` body as an Error message; fall back to the status. */
async function failure(res: Response): Promise<Error> {
  const body = (await res.json().catch(() => null)) as { error?: string; detail?: string } | null;
  return new Error(body?.detail ?? body?.error ?? `Request failed (${res.status})`);
}

/** The caller's saved RSVP, or `null` if they haven't submitted one yet (API returns 404). */
export async function getMyRsvp(): Promise<RsvpRecord | null> {
  const res = await authed('/rsvp');
  if (res.status === 404) return null;
  if (!res.ok) throw await failure(res);
  return (await res.json()) as RsvpRecord;
}

/** Create or replace the caller's RSVP; returns the persisted record. */
export async function submitRsvp(input: RsvpInput): Promise<RsvpRecord> {
  const res = await authed('/rsvp', { method: 'POST', body: JSON.stringify(input) });
  if (!res.ok) throw await failure(res);
  return (await res.json()) as RsvpRecord;
}
